import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FiCheckCircle, FiXCircle, FiInfo } from "react-icons/fi";

// ── Palette (matches the TOEFL Reading / Listening pages) ───────────────────
const C = {
  green: "#05914f",
  greenDark: "#0c572e",
  greenText: "#0c3d24",
  border: "#c8e6d4",
  soft: "#f4faf7",
  muted: "#6b7a74",
  red: "#d93646",
  amber: "#d9822b",
  ok: "#19a96b",
};

// Colour for a 0-100 percentage
const colorForPercent = (p) => (p >= 70 ? C.green : p >= 40 ? C.amber : C.red);

// Study advice per question type (used for personalized recommendations)
const CATEGORY_TIPS = {
  "Main Ideas":
    "Listen for the topic in the first sentence or two, and notice what the speakers keep coming back to. Ask yourself, \"What is this mostly about?\" before looking at the options.",
  Details:
    "Practise taking quick notes on names, numbers, places and reasons. Replay a recording afterwards and check each detail you wrote down.",
  "Speaker Purpose":
    "Pay attention to tone and phrases such as \"I wish I could…\" or \"That's a relief!\". They often show what the speaker really means, not just what they say.",
  Relationships:
    "Focus on who will do what next, and why. Practise predicting the next action or suggestion from the speakers' words before you read the answer choices.",
  // ── TOEFL iBT 2026 task types ──
  "Complete the Words":
    "Read the whole sentence before filling a gap, and check that each completed word fits grammatically (plural, tense, part of speech). Build spelling accuracy with common academic words.",
  "Read in Daily Life":
    "Practise with real notices, emails, schedules and menus. Scan for dates, prices and conditions, and notice what the writer wants the reader to do.",
  "Academic Passage":
    "Identify the main idea of each paragraph first. For vocabulary questions, substitute each option into the sentence; for inference questions, look for what the passage implies but does not state.",
  "Choose a Response":
    "Listen for the function of the line (a request, a complaint, a question) and choose the reply that responds to it naturally, not the one that repeats its words.",
  Conversation:
    "Track why the speakers are talking and what problem they are solving. Note any suggestion or next step near the end of the conversation.",
  Announcement:
    "Listen for what is changing, when, and what listeners are asked to do. Announcements usually state the main point in the first sentence.",
  "Academic Talk":
    "Note the topic, key terms and examples. Ask yourself why the speaker mentions each example, as purpose questions are common.",
};

// ── Always open the page from the very top ──────────────────────────────────
const scrollToTop = () => {
  window.scrollTo(0, 0);
  if (document.documentElement) document.documentElement.scrollTop = 0;
  if (document.body) document.body.scrollTop = 0;
};

// ── Responsive hook (matches ToeflReading.js / ToeflListening.js) ───────────
function useWindowWidth() {
  const [width, setWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1024
  );
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return width;
}

function ToeflResultPage() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const width = useWindowWidth();
  const isMobile = width < 768;
  const reviewRef = useRef(null);

  // Open from the top (heading never hidden behind the navbar)
  useLayoutEffect(() => {
    const hasRestoration = "scrollRestoration" in window.history;
    const prev = hasRestoration ? window.history.scrollRestoration : null;
    if (hasRestoration) window.history.scrollRestoration = "manual";

    scrollToTop();
    const t1 = setTimeout(scrollToTop, 0);
    const t2 = setTimeout(scrollToTop, 150);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      if (hasRestoration) window.history.scrollRestoration = prev;
    };
  }, []);

  const {
    correctCount = 0,
    total = 0,
    testType = "Test",
    breakdown = null,
    toeflScore = null,
    compositeScore = null,
    band = null,
    wordCount = null,
    warnings = [],
    saveStatus = null,
    // TOEFL iBT 2026 fields
    bandScore = null,
    cefr = null,
    bandDescription = null,
    adaptivePath = null,
    taskScores = null,
    review = null,
  } = state || {};
  const hasBand = typeof bandScore === "number";

  const saveFailedMessage =
    saveStatus === "unauthorized"
      ? "Your login session expired, so this result was not saved to your profile. Log in again and retake the test to save it."
      : saveStatus === "network"
      ? "Could not reach the server, so this result was not saved to your profile. Check your connection and try again."
      : saveStatus === "error"
      ? "The server could not save this result to your profile. Please try again."
      : null;

  // Writing results come from a rubric (0-6 composite / ~30 scaled score,
  // per-criterion breakdown object), not a right/wrong question count —
  // MCQ tests (Reading/Listening) are the only ones with a percentage.
  // Writing & Speaking (2026) send `taskScores`; older Writing saves sent a
  // criteria object as `breakdown`. Both render as a rubric result.
  const criteriaBreakdown = Array.isArray(taskScores)
    ? taskScores
    : testType === "Writing" && breakdown && !Array.isArray(breakdown)
    ? Object.values(breakdown)
    : null;
  const isWritingResult = !!criteriaBreakdown;
  const reviewItems = Array.isArray(review) ? review : Array.isArray(breakdown) ? breakdown : null;
  const percentage = total ? Math.round((correctCount / total) * 100) : 0;
  // Rubric scores arrive as raw averages (e.g. 2.3166…) — show one decimal.
  const fmt = (n) => (typeof n === "number" ? Math.round(n * 10) / 10 : n ?? 0);
  const incorrectCount = Math.max(total - correctCount, 0);

  const scoreColor = hasBand
    ? colorForPercent((bandScore / 6) * 100)
    : isWritingResult
    ? C.green
    : colorForPercent(percentage);

  const performanceLabel =
    percentage >= 80
      ? "Excellent"
      : percentage >= 60
      ? "Good"
      : percentage >= 40
      ? "Fair"
      : "Needs Practice";

  // ── Performance by question type (only when questions carry a `category`) ──
  const categoryStats = (() => {
    if (isWritingResult || !Array.isArray(breakdown)) return [];
    const map = {};
    breakdown.forEach((item) => {
      if (!item.category) return;
      if (!map[item.category]) map[item.category] = { correct: 0, total: 0 };
      map[item.category].total += 1;
      if (item.isCorrect) map[item.category].correct += 1;
    });
    return Object.entries(map).map(([label, v]) => ({
      label,
      correct: v.correct,
      total: v.total,
      percent: Math.round((v.correct / v.total) * 100),
    }));
  })();

  const sortedStats = [...categoryStats].sort((a, b) => a.percent - b.percent);
  const weakest = sortedStats.length > 0 ? sortedStats[0] : null;
  const strongest =
    sortedStats.length > 0 ? sortedStats[sortedStats.length - 1] : null;

  const unansweredCount = Array.isArray(breakdown)
    ? breakdown.filter((b) => b.userAnswerText === "No answer").length
    : 0;

  // ── Short performance explanation ──────────────────────────────────────────
  const baseExplanation =
    percentage >= 80
      ? "Strong performance. You understood most of the recordings and answered with confidence."
      : percentage >= 60
      ? "Good progress. You followed the main points, but a few details or implied meanings slipped by."
      : percentage >= 40
      ? "You have the basics, but your accuracy is inconsistent. Targeted practice should lift your score."
      : "This round was challenging. Most answers missed the key information, so build up the fundamentals before another full test.";

  const explanationExtra =
    strongest && weakest && strongest.percent > weakest.percent
      ? `Strongest area: ${strongest.label} (${strongest.percent}%). Needs the most work: ${weakest.label} (${weakest.percent}%).`
      : "";

  // ── Personalized recommendations ───────────────────────────────────────────
  const recommendations = [];

  sortedStats
    .filter((c) => c.percent < 70)
    .slice(0, 3)
    .forEach((c) => {
      recommendations.push({
        title: `Strengthen ${c.label} (${c.percent}%)`,
        text:
          CATEGORY_TIPS[c.label] ||
          `You answered ${c.correct} of ${c.total} ${c.label} questions correctly. Practise more questions of this type.`,
      });
    });

  if (incorrectCount > 0) {
    recommendations.push({
      title: `Review your ${incorrectCount} incorrect answer${incorrectCount === 1 ? "" : "s"}`,
      text: "Go through the Question Review below and, for each miss, work out why the correct option fits better than the one you chose.",
    });
  }

  if (unansweredCount > 0) {
    recommendations.push({
      title: `${unansweredCount} question${unansweredCount === 1 ? " was" : "s were"} left unanswered`,
      text: "There is no penalty for guessing on this test, so always choose an answer, even when you are unsure.",
    });
  }

  if (categoryStats.length === 0) {
    recommendations.push({
      title: "Practise active listening",
      text: "Before each recording, read the question first. Then listen for the answer instead of trying to follow every word.",
    });
  }

  if (percentage >= 70 && recommendations.length <= 1) {
    recommendations.push({
      title: "Keep your momentum",
      text: "Your accuracy is solid. Try a full timed test to build stamina and keep your focus for the whole session.",
    });
  } else {
    recommendations.push({
      title: `Take another ${testType.toLowerCase()} test`,
      text: "Short, regular practice works better than one long session. Retake a test after reviewing your mistakes to check your progress.",
    });
  }

  // ── Score ring geometry ────────────────────────────────────────────────────
  const ringSize = isMobile ? 140 : 172;
  const ringStroke = isMobile ? 10 : 12;
  const ringRadius = (ringSize - ringStroke) / 2;
  const ringCircumference = 2 * Math.PI * ringRadius;
  const ringPercent = hasBand
    ? Math.min(Math.max((bandScore / 6) * 100, 0), 100)
    : isWritingResult
    ? Math.min(Math.max(((toeflScore ?? 0) / 30) * 100, 0), 100)
    : Math.min(Math.max(percentage, 0), 100);
  const ringOffset = ringCircumference * (1 - ringPercent / 100);

  // ── Shared styles ──────────────────────────────────────────────────────────
  const pageStyle = {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "20px",
    boxSizing: "border-box",
    padding: isMobile ? "100px 14px 40px" : "130px 24px 56px",
    overflowX: "hidden",
  };

  const cardStyle = {
    width: "100%",
    maxWidth: "760px",
    background: "#ffffff",
    border: `1px solid ${C.border}`,
    borderRadius: "16px",
    boxShadow: "0 6px 22px rgba(12, 87, 46, 0.08)",
    boxSizing: "border-box",
  };

  const primaryBtn = {
    flex: 1,
    padding: isMobile ? "13px 20px" : "14px 28px",
    background: `linear-gradient(135deg, ${C.green} 0%, ${C.greenDark} 100%)`,
    color: "#fff",
    border: "none",
    borderRadius: "50px",
    fontSize: isMobile ? "0.95rem" : "1rem",
    fontWeight: 700,
    letterSpacing: "0.2px",
    cursor: "pointer",
    boxShadow: "0 6px 16px rgba(12, 87, 46, 0.28)",
  };

  const secondaryBtn = {
    flex: 1,
    padding: isMobile ? "13px 20px" : "14px 28px",
    background: "#ffffff",
    color: C.greenText,
    border: `1.5px solid ${C.border}`,
    borderRadius: "50px",
    fontSize: isMobile ? "0.95rem" : "1rem",
    fontWeight: 600,
    letterSpacing: "0.2px",
    cursor: "pointer",
  };

  const sectionTitle = {
    margin: "0 0 6px",
    color: C.greenText,
    fontSize: isMobile ? "1.1rem" : "1.35rem",
    fontWeight: 800,
    textAlign: "left",
  };

  const sectionSub = {
    margin: "0 0 18px",
    color: C.muted,
    fontSize: isMobile ? "0.82rem" : "0.9rem",
    textAlign: "left",
  };

  // ── No data (e.g. page opened directly / refreshed) ───────────────────────
  if (!state) {
    return (
      <div style={pageStyle}>
        <div
          style={{
            ...cardStyle,
            maxWidth: "480px",
            padding: isMobile ? "28px 20px" : "40px 32px",
            textAlign: "center",
          }}
        >
          <h2 style={{ ...sectionTitle, textAlign: "center" }}>
            No results to show
          </h2>
          <p style={{ color: C.muted, margin: "0 0 22px", lineHeight: 1.6 }}>
            Complete a test to see your results here.
          </p>
          <button style={{ ...primaryBtn, width: "100%" }} onClick={() => navigate("/")}>
            Back to Tests
          </button>
        </div>
      </div>
    );
  }

  // Stat tiles (MCQ tests only)
  const statTiles = [
    {
      label: "Correct",
      value: `${correctCount}/${total}`,
      color: C.ok,
      bg: "rgba(25, 169, 107, 0.08)",
      border: "rgba(25, 169, 107, 0.25)",
    },
    {
      label: "Incorrect",
      value: `${incorrectCount}/${total}`,
      color: C.red,
      bg: "rgba(217, 54, 70, 0.07)",
      border: "rgba(217, 54, 70, 0.22)",
    },
    {
      label: "Accuracy",
      value: `${percentage}%`,
      color: scoreColor,
      bg: C.soft,
      border: C.border,
    },
  ];

  const hasReview = Array.isArray(reviewItems) && reviewItems.length > 0;

  // CEFR level, comparable 0–30 score and adaptive route (2026 format)
  const bandInfo = hasBand && (
    <div style={{ margin: "0 0 22px" }}>
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "8px",
          flexWrap: "wrap",
          justifyContent: "center",
          fontSize: isMobile ? "0.9rem" : "0.98rem",
          color: C.greenText,
          fontWeight: 600,
        }}
      >
        <span
          style={{
            background: C.soft,
            border: `1px solid ${C.border}`,
            borderRadius: "50px",
            padding: "3px 12px",
            fontWeight: 800,
          }}
        >
          CEFR {cefr}
        </span>
        <span>
          Comparable score:{" "}
          <strong style={{ color: C.greenDark }}>≈ {fmt(toeflScore)}/30</strong>
        </span>
      </div>
      {bandDescription && (
        <p
          style={{
            fontSize: isMobile ? "0.85rem" : "0.93rem",
            color: C.muted,
            margin: "10px auto 0",
            maxWidth: "520px",
            lineHeight: 1.6,
          }}
        >
          {bandDescription}
        </p>
      )}
      {adaptivePath && (
        <p style={{ fontSize: "0.8rem", color: C.muted, margin: "8px auto 0", maxWidth: "520px", lineHeight: 1.6 }}>
          {adaptivePath === "upper"
            ? "Module 1 routed you to the harder Module 2, so the full band range (up to 6) was available."
            : "Module 1 routed you to the easier Module 2, which caps the band at 4.5. Raise your Module 1 accuracy to unlock the harder module."}
        </p>
      )}
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "5px",
          marginTop: "6px",
          fontSize: "0.8rem",
          color: C.muted,
        }}
      >
        <FiInfo size={13} />
        Practice-test estimate on the TOEFL iBT 1–6 scale
      </div>
    </div>
  );

  // Rows for the breakdown card (falls back to a single overall bar)
  const breakdownRows =
    categoryStats.length > 0
      ? categoryStats
      : [
          {
            label: "Overall accuracy",
            correct: correctCount,
            total,
            percent: percentage,
          },
        ];

  return (
    <div style={pageStyle}>
      {/* ───────────── SUMMARY CARD ───────────── */}
      <div
        style={{
          ...cardStyle,
          position: "relative",
          overflow: "hidden",
          background: "linear-gradient(180deg, #f4faf7 0%, #ffffff 42%)",
          padding: isMobile ? "28px 18px 24px" : "40px 44px 34px",
          textAlign: "center",
        }}
      >
        {/* Accent bar: clipped by the card's rounded corners, so it stays straight */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "5px",
            background: `linear-gradient(90deg, ${C.green}, ${C.ok})`,
          }}
        />

        {/* Eyebrow chip */}
        <div
          style={{
            display: "inline-block",
            fontSize: "0.72rem",
            fontWeight: 700,
            letterSpacing: "1.6px",
            textTransform: "uppercase",
            color: C.green,
            background: "#ffffff",
            border: `1px solid ${C.border}`,
            borderRadius: "50px",
            padding: "5px 14px",
            marginBottom: "14px",
          }}
        >
          {hasBand ? "TOEFL iBT" : "TOEFL PBT"} &nbsp;•&nbsp; {testType}
        </div>

        <h2
          style={{
            margin: "0 0 24px",
            color: C.greenText,
            fontSize: isMobile ? "1.45rem" : "1.95rem",
            fontWeight: 800,
            letterSpacing: "-0.4px",
          }}
        >
          TOEFL {testType} Results
        </h2>

        {/* Score ring */}
        <div
          style={{
            position: "relative",
            width: `${ringSize}px`,
            height: `${ringSize}px`,
            margin: "0 auto 18px",
          }}
        >
          <svg
            width={ringSize}
            height={ringSize}
            viewBox={`0 0 ${ringSize} ${ringSize}`}
            style={{ transform: "rotate(-90deg)", display: "block" }}
          >
            <circle
              cx={ringSize / 2}
              cy={ringSize / 2}
              r={ringRadius}
              fill="#ffffff"
              stroke="#e4f1ea"
              strokeWidth={ringStroke}
            />
            <circle
              cx={ringSize / 2}
              cy={ringSize / 2}
              r={ringRadius}
              fill="none"
              stroke={scoreColor}
              strokeWidth={ringStroke}
              strokeLinecap="round"
              strokeDasharray={ringCircumference}
              strokeDashoffset={ringOffset}
              style={{ transition: "stroke-dashoffset 0.8s ease" }}
            />
          </svg>

          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                fontSize: isMobile ? "2.1rem" : "2.7rem",
                fontWeight: 800,
                color: scoreColor,
                lineHeight: 1,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {hasBand ? (
                <>
                  {bandScore}
                  <span
                    style={{
                      fontSize: isMobile ? "0.95rem" : "1.1rem",
                      color: C.muted,
                      fontWeight: 700,
                    }}
                  >
                    /6
                  </span>
                </>
              ) : isWritingResult ? (
                <>
                  {fmt(toeflScore)}
                  <span
                    style={{
                      fontSize: isMobile ? "0.95rem" : "1.1rem",
                      color: C.muted,
                      fontWeight: 700,
                    }}
                  >
                    /30
                  </span>
                </>
              ) : (
                `${percentage}%`
              )}
            </div>
            <div
              style={{
                marginTop: "6px",
                fontSize: "0.66rem",
                fontWeight: 700,
                letterSpacing: "1.4px",
                textTransform: "uppercase",
                color: C.muted,
              }}
            >
              {hasBand ? "Band Score" : isWritingResult ? "Scaled Score" : "Accuracy"}
            </div>
          </div>
        </div>

        {saveFailedMessage && (
          <div
            style={{
              background: "#fef2f2",
              border: "1px solid #fecaca",
              color: "#dc2626",
              borderRadius: "10px",
              padding: "10px 14px",
              fontSize: "0.85rem",
              lineHeight: "1.5",
              marginBottom: "1rem",
              textAlign: "left",
            }}
          >
            ⚠️ {saveFailedMessage}
          </div>
        )}

        {isWritingResult ? (
          <>
            {hasBand ? (
              bandInfo
            ) : (
              <p
                style={{
                  fontSize: isMobile ? "1rem" : "1.1rem",
                  color: C.muted,
                  margin: "0 0 6px",
                }}
              >
                Rubric composite:{" "}
                <strong style={{ color: C.greenText }}>{fmt(compositeScore)}/6</strong>
                {wordCount !== null && <> &nbsp;·&nbsp; {wordCount} words</>}
              </p>
            )}

            {band && (
              <p
                style={{
                  fontSize: isMobile ? "0.95rem" : "1.05rem",
                  color: C.green,
                  fontWeight: 700,
                  margin: "6px 0 4px",
                }}
              >
                {band.band}
              </p>
            )}

            {band && (
              <p
                style={{
                  fontSize: isMobile ? "0.85rem" : "0.93rem",
                  color: C.muted,
                  margin: "0 auto 8px",
                  maxWidth: "520px",
                  lineHeight: 1.6,
                }}
              >
                {band.description}
              </p>
            )}

            <div
              style={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                gap: "12px",
                marginTop: "20px",
              }}
            >
              <button style={primaryBtn} onClick={() => navigate("/profile")}>
                Go to Profile
              </button>
              <button style={secondaryBtn} onClick={() => navigate("/")}>
                Back to Tests
              </button>
            </div>
          </>
        ) : (
          <>
            <p
              style={{
                fontSize: isMobile ? "0.98rem" : "1.1rem",
                color: C.muted,
                margin: "0 0 12px",
              }}
            >
              <strong style={{ color: C.greenText }}>{correctCount}</strong> of{" "}
              <strong style={{ color: C.greenText }}>{total}</strong> correct
            </p>

            {/* Performance label */}
            <div
              style={{
                display: "inline-block",
                fontSize: isMobile ? "0.82rem" : "0.88rem",
                fontWeight: 700,
                color: scoreColor,
                background: `${scoreColor}14`,
                border: `1px solid ${scoreColor}40`,
                borderRadius: "50px",
                padding: "5px 16px",
                marginBottom: "14px",
              }}
            >
              {performanceLabel}
            </div>

            {/* Short performance explanation */}
            <p
              style={{
                maxWidth: "520px",
                margin: "0 auto 22px",
                fontSize: isMobile ? "0.9rem" : "0.97rem",
                lineHeight: 1.65,
                color: C.muted,
              }}
            >
              {baseExplanation}
              {explanationExtra && (
                <>
                  <br />
                  <span style={{ color: C.greenText, fontWeight: 600 }}>
                    {explanationExtra}
                  </span>
                </>
              )}
            </p>

            {/* Stat tiles */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: isMobile ? "8px" : "14px",
                margin: "0 0 18px",
              }}
            >
              {statTiles.map((s) => (
                <div
                  key={s.label}
                  style={{
                    background: s.bg,
                    border: `1px solid ${s.border}`,
                    borderRadius: "12px",
                    padding: isMobile ? "12px 4px" : "16px 8px",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      fontSize: isMobile ? "1.2rem" : "1.55rem",
                      fontWeight: 800,
                      color: s.color,
                      lineHeight: 1.15,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {s.value}
                  </div>
                  <div
                    style={{
                      fontSize: isMobile ? "0.62rem" : "0.7rem",
                      fontWeight: 700,
                      letterSpacing: "0.9px",
                      textTransform: "uppercase",
                      color: C.muted,
                      marginTop: "6px",
                    }}
                  >
                    {s.label}
                  </div>
                </div>
              ))}
            </div>

            {/* Band score details (2026 format) */}
            {hasBand && bandInfo}

            {/* Estimated scaled score */}
            {!hasBand && toeflScore !== null && (
              <div style={{ margin: "0 0 24px" }}>
                <div
                  style={{
                    fontSize: isMobile ? "0.95rem" : "1.02rem",
                    color: C.greenText,
                    fontWeight: 600,
                  }}
                >
                  Estimated scaled score:{" "}
                  <strong style={{ color: C.greenDark }}>{fmt(toeflScore)}/30</strong>
                </div>
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "5px",
                    marginTop: "4px",
                    fontSize: "0.8rem",
                    color: C.muted,
                  }}
                >
                  <FiInfo size={13} />
                  Practice-test estimate
                </div>
              </div>
            )}

            {/* Actions: Review Answers is the primary action */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                marginTop: toeflScore !== null ? 0 : "8px",
              }}
            >
              {hasReview && (
                <button
                  style={{ ...primaryBtn, flex: "none", width: "100%" }}
                  onClick={() =>
                    reviewRef.current?.scrollIntoView({
                      behavior: "smooth",
                      block: "start",
                    })
                  }
                >
                  Review Answers
                </button>
              )}

              <div
                style={{
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  gap: "12px",
                }}
              >
                <button
                  style={hasReview ? secondaryBtn : primaryBtn}
                  onClick={() => navigate("/")}
                >
                  Try Another Test
                </button>
                <button style={secondaryBtn} onClick={() => navigate("/profile")}>
                  Go to Profile
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ───────────── PERFORMANCE BREAKDOWN ───────────── */}
      {!isWritingResult && (
        <div
          style={{
            ...cardStyle,
            padding: isMobile ? "20px 16px" : "30px 34px",
          }}
        >
          <h3 style={sectionTitle}>Performance Breakdown</h3>
          <p style={sectionSub}>
            {categoryStats.length > 0
              ? "How you did on each type of question."
              : "Your overall accuracy on this test."}
          </p>

          {breakdownRows.map((row, i) => {
            const col = colorForPercent(row.percent);
            return (
              <div
                key={row.label}
                style={{
                  marginBottom: i === breakdownRows.length - 1 ? 0 : "16px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                    marginBottom: "6px",
                    fontSize: isMobile ? "0.88rem" : "0.95rem",
                  }}
                >
                  <span style={{ color: C.greenText, fontWeight: 600 }}>
                    {row.label}
                    <span style={{ color: C.muted, fontWeight: 400 }}>
                      {" "}
                      ({row.correct}/{row.total})
                    </span>
                  </span>
                  <span
                    style={{
                      color: col,
                      fontWeight: 800,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {row.percent}%
                  </span>
                </div>
                <div
                  style={{
                    height: "8px",
                    background: "#e4f1ea",
                    borderRadius: "4px",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: `${row.percent}%`,
                      height: "100%",
                      background: col,
                      borderRadius: "4px",
                      transition: "width 0.6s ease",
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ───────────── PERSONALIZED RECOMMENDATIONS ───────────── */}
      {!isWritingResult && (
        <div
          style={{
            ...cardStyle,
            padding: isMobile ? "20px 16px" : "30px 34px",
          }}
        >
          <h3 style={sectionTitle}>Your Improvement Plan</h3>
          <p style={sectionSub}>Recommendations based on your answers.</p>

          {recommendations.map((rec, i) => (
            <div
              key={rec.title}
              style={{
                display: "flex",
                gap: "14px",
                alignItems: "flex-start",
                padding: isMobile ? "14px 0" : "16px 0",
                borderTop: `1px solid ${C.border}`,
                textAlign: "left",
              }}
            >
              <div
                style={{
                  flexShrink: 0,
                  width: "28px",
                  height: "28px",
                  borderRadius: "50%",
                  background: C.soft,
                  border: `1px solid ${C.border}`,
                  color: C.greenDark,
                  fontWeight: 800,
                  fontSize: "0.85rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {i + 1}
              </div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div
                  style={{
                    color: C.greenText,
                    fontWeight: 700,
                    fontSize: isMobile ? "0.92rem" : "1rem",
                    marginBottom: "4px",
                  }}
                >
                  {rec.title}
                </div>
                <div
                  style={{
                    color: C.muted,
                    fontSize: isMobile ? "0.84rem" : "0.92rem",
                    lineHeight: 1.65,
                  }}
                >
                  {rec.text}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ───────────── PER-QUESTION REVIEW ───────────── */}
      {hasReview && (
        <div
          ref={reviewRef}
          style={{
            ...cardStyle,
            scrollMarginTop: isMobile ? "90px" : "110px",
            padding: isMobile ? "20px 16px" : "30px 34px",
          }}
        >
          <h3 style={sectionTitle}>Question Review</h3>
          <p
            style={{
              margin: "0 0 14px",
              color: C.muted,
              fontSize: isMobile ? "0.82rem" : "0.9rem",
              textAlign: "left",
            }}
          >
            Review each question with your answer and the correct answer.
          </p>

          {reviewItems.map((item, idx) => (
            <div
              key={item.number ?? idx}
              style={{
                display: "flex",
                gap: "12px",
                alignItems: "flex-start",
                padding: isMobile ? "14px 0" : "16px 0",
                borderTop: `1px solid ${C.border}`,
                textAlign: "left",
              }}
            >
              <div style={{ flexShrink: 0, marginTop: "2px" }}>
                {item.isCorrect ? (
                  <FiCheckCircle size={20} color={C.ok} />
                ) : (
                  <FiXCircle size={20} color={C.red} />
                )}
              </div>

              <div style={{ minWidth: 0, flex: 1, wordBreak: "break-word" }}>
                <div
                  style={{
                    color: C.greenText,
                    fontWeight: 700,
                    fontSize: isMobile ? "0.92rem" : "1rem",
                    marginBottom: "6px",
                  }}
                >
                  Q{item.number}. {item.questionText}
                </div>

                <div
                  style={{
                    fontSize: isMobile ? "0.83rem" : "0.9rem",
                    lineHeight: 1.65,
                    color: C.muted,
                  }}
                >
                  <div>
                    Your answer:{" "}
                    <strong style={{ color: item.isCorrect ? C.ok : C.red }}>
                      {item.userAnswerText || "No answer"}
                    </strong>
                  </div>

                  {!item.isCorrect && item.correctAnswerText && (
                    <div>
                      Correct answer:{" "}
                      <strong style={{ color: C.ok }}>{item.correctAnswerText}</strong>
                    </div>
                  )}

                  {item.explanation && <div>Explanation: {item.explanation}</div>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ───────────── WRITING RUBRIC BREAKDOWN ───────────── */}
      {criteriaBreakdown && (
        <>
          {warnings.length > 0 && (
            <div
              style={{
                ...cardStyle,
                padding: isMobile ? "16px" : "22px 28px",
                textAlign: "left",
              }}
            >
              {warnings.map((w, i) => (
                <p
                  key={i}
                  style={{
                    color: C.muted,
                    fontSize: isMobile ? "0.84rem" : "0.92rem",
                    margin: i === 0 ? "0 0 6px" : "6px 0",
                    lineHeight: 1.6,
                  }}
                >
                  {w}
                </p>
              ))}
            </div>
          )}

          <div
            style={{
              ...cardStyle,
              padding: isMobile ? "20px 16px" : "30px 34px",
            }}
          >
            <h3 style={{ ...sectionTitle, marginBottom: "14px" }}>Rubric Breakdown</h3>

            {criteriaBreakdown.map((c, idx) => (
              <div
                key={c.label ?? idx}
                style={{
                  padding: isMobile ? "14px 0" : "16px 0",
                  borderTop: `1px solid ${C.border}`,
                  textAlign: "left",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                    gap: "12px",
                    marginBottom: "6px",
                  }}
                >
                  <span
                    style={{
                      color: C.greenText,
                      fontWeight: 700,
                      fontSize: isMobile ? "0.92rem" : "1rem",
                    }}
                  >
                    {c.label}
                  </span>

                  <span
                    style={{
                      color: C.green,
                      fontWeight: 800,
                      fontSize: isMobile ? "0.9rem" : "0.97rem",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {fmt(c.score)}/{c.max ?? 6}
                  </span>
                </div>

                {Array.isArray(c.diagnostics) && c.diagnostics.length > 0 && (
                  <ul
                    style={{
                      margin: 0,
                      paddingLeft: "1.1rem",
                      color: C.muted,
                      fontSize: isMobile ? "0.83rem" : "0.9rem",
                      lineHeight: 1.65,
                    }}
                  >
                    {c.diagnostics.map((d, dIdx) => (
                      <li key={dIdx}>{d}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default ToeflResultPage;